import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { getUser } from '@cast/infrastructure-auth';
import { OrganizationType } from '@cast/types';

import { validateUserRemoval } from 'features/organization/management/utils/rbac-validation';
import { ORGANIZATION_USERS_QUERY_KEY } from 'hooks/queries/organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  useRbacRoleBindings,
  useRbacRolesQuery,
} from '@cast/shared-rbac-data-access';

type RemoveUserParams = {
  orgId: string;
  userId: string;
};

type UseRemoveUserFromOrganizationWithValidationMutation = UseMutationResult<
  AxiosResponse<unknown>,
  unknown,
  RemoveUserParams
>;

export const useRemoveUserFromOrganizationWithValidationMutation = (
  onSuccess?: () => void,
  options?: Omit<
    UseMutationOptions<AxiosResponse<unknown>, unknown, RemoveUserParams>,
    'mutationFn'
  >
): UseRemoveUserFromOrganizationWithValidationMutation => {
  const queryClient = useQueryClient();
  const currentUser = getUser();
  const { currentOrganization, organizations } = useOrganizations();
  const { roleBindings, isLoadingRoleBindings } = useRbacRoleBindings({
    enabled: true,
    organizationId: currentOrganization?.id,
  });
  const { data: roles = [], isLoading: isLoadingRoles } = useRbacRolesQuery({
    type: 'DEFAULT',
  });

  return useMutation({
    mutationFn: async ({ orgId, userId }: RemoveUserParams) => {
      // Wait for data to load if needed
      if (isLoadingRoleBindings || isLoadingRoles) {
        throw new Error('Loading role data, please try again');
      }

      if (!currentUser?.guid || !roleBindings || !roles.length) {
        throw new Error('Unable to validate user removal');
      }

      // Find the parent organization to determine if current org is a child
      const parentOrg = organizations?.find(
        (org) => org.type === OrganizationType.ENTERPRISE
      );
      const isChildOrganization =
        currentOrganization?.type !== OrganizationType.ENTERPRISE &&
        !!parentOrg;

      // Validate the removal
      const validation = validateUserRemoval({
        userId,
        currentUserId: currentUser.guid,
        roleBindings,
        roles,
        isChildOrganization,
        organizationScopeId: orgId,
      });

      if (!validation.isValid) {
        throw new Error(validation.errorMessage);
      }

      // If validation passes, proceed with the API call
      return apiClient.user.deleteOrganizationUser({
        organizationId: orgId,
        userId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_USERS_QUERY_KEY],
      });
      notify.success('You have left the organization');
      onSuccess?.();
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error));
    },
    ...options,
  });
};
